import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Table, Button, Input, Space, Tag, message, Tooltip } from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import TagModal from "../../components/Tags/TagModal";
import Layout from '../../Layout'
import ChangePositionModal from "./ChangePositionModal";
import {
    Edit,
    X,
} from "lucide-react";
import { mixDate, mixDelV1, mixGetV1, mixPostV1, mixPutV1 } from "services/mixin";
import { error } from "console";
import useAuthStore from "../../store/authStore";
import useTableColumnSettings, { SettingColumn } from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

const { Search } = Input;

interface TagItem {
    id: number;
    code: string;
    name: string;
    color: string;
    creator: {
        email: string;
        fullname: string;
    };
    description: string;
    position: number;
    created_at?: string | null;
}

export default function CustomerTags() {
    const { t } = useTranslation();
    // Phân quyền thao tác: chỉ có quyền xem thì không hiện nút thêm/sửa/xóa
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("chat_tags.create");
    const canEdit = isSuperAdmin || hasPermission("chat_tags.edit");
    const canDelete = isSuperAdmin || hasPermission("chat_tags.delete");
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState<"add" | "edit">("add");
    const [editingItem, setEditingItem] = useState<TagItem | null>(null);

    // Dùng chung cho tham số limit gửi lên API và pageSize của bảng:
    // API bỏ qua page nếu không có limit, hai chỗ lệch nhau là phân trang sai.
    const PAGE_SIZE = 10;

    const [data, setData] = useState<TagItem[]>([]);
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(1)
    const [loading, setLoading] = useState(false);
    const [sortParam, setSortParam] = useState<string>("position,asc");


    const [positionOpen, setPositionOpen] = useState(false);

    const [actionModal, setActionModal] = useState<{ open: boolean; type: "approve" | "reject" | null; id: number | null }>({ open: false, type: null, id: null });

    const openDeleteModal = (id: number) => {
        setActionModal({
            open: true,
            type: "reject",
            id,
        });
    };

    const handleCloseActionModal = () => {
        setActionModal({
            open: false,
            type: null,
            id: null,
        });
    };

    const handleConfirmAction = async () => {
        if (actionModal.id !== null) {
            const res = await mixDelV1(`/api/v1/be/customer-care-status/${actionModal.id}`, { _method: "DELETE", })

            if (res.status) {
                message.success(t('care_status.delete_success'));
                getList()
            } else {
                message.error(t('care_status.delete_failed'));
            }
        }

        handleCloseActionModal();
    };

    const openAdd = () => {
        setMode("add");
        setEditingItem(null);
        setModalOpen(true);
    };

    const openEdit = (record: TagItem) => {
        setMode("edit");
        setEditingItem(record);
        setModalOpen(true);
    };

    const handleSubmit = async (values: any) => {

        const param = { ...values }

        if (mode === "add") {
            const res = await mixPostV1('/api/v1/be/customer-care-status', param)

            if (res.status) {
                message.success(t('care_status.add_success'));
                getList()
            } else {
                message.error(t('care_status.add_failed'));
            }
        } else if (mode === "edit" && editingItem) {
            const res = await mixPutV1(`/api/v1/be/customer-care-status/${editingItem.id}`, param)

            if (res.status) {
                message.success(t('care_status.edit_success'));
                getList()
            } else {
                message.error(t('care_status.edit_failed'));
            }
        }

        setModalOpen(false);
    };

    const columns: SettingColumn<TagItem>[] = [
        {
            title: "Id",
            dataIndex: "id",
            key: "id",
            sorter: true,
        },
        {
            title: t('care_status.col_status'),
            key: "name",
            sorter: true,
            render: (_, record) => (
                <Tag color={record.color}>{record.name}</Tag>
            ),
        },
        {
            title: t('care_status.col_created_at'),
            key: "created_at",
            sorter: true,
            render: (_, record) => mixDate(record.created_at, { hour: true }),
        },
        {
            key: "creator",
            title: t('care_status.col_creator'),
            render: (_, record) => (record.creator?.fullname),
        },
        {
            title: t('care_status.col_description'),
            dataIndex: "description",
            width: 300,
            render: (text: string) => (
                <Tooltip title={text}>
                    <div
                        style={{
                            maxWidth: 300,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {text}
                    </div>
                </Tooltip>
            ),
        },
        {
            title: t('care_status.col_position'),
            dataIndex: "position",
            key: "position",
            sorter: true,
        },
        {
            key: "actions",
            title: t('care_status.col_action'),
            fixed: "right",
            lockVisible: true,
            render: (_, record) => (
                <div className="flex items-center gap-3">
                    <div className="flex items-center space-x-3 gap-2">
                        {canEdit && (
                            <button
                                onClick={() => openEdit(record)}
                                className="!text-[#3e91ab] hover:text-yellow-700 bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 p-1.5 rounded-md transition-colors"
                                title="View Details"
                            >
                                <Edit className="h-4 w-4" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => openDeleteModal(record.id)}
                                className="!text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 p-1.5 rounded-md transition-colors"
                                title="Reject"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "care_status_list",
        (canEdit || canDelete) ? columns : columns.filter((c) => c.key !== "actions")
    );

    // ---------------  API------------------------
    const getList = async () => {
        setLoading(true);

        const param = { page, limit: PAGE_SIZE, sort: sortParam }

        const res = await mixGetV1('/api/v1/be/customer-care-statuses', param)

        if (res.status) {
            setData(res.data.items)
            setTotal(res.data.total)
        } else {
            message.error(res.error)
        }
        setLoading(false);
    }
    const changePosition = async (list) => {
        setData(list);
        setPositionOpen(false);

        const param = {
            list: list.map((item: any) => ({
                id: item.id,
                position: item.position,
            }))
        }

        // console.log(param)

        const res = await mixPostV1('/api/v1/be/customer-care-status/update-position', param)

        if (res.status) {
            message.success(t('care_status.change_position_success'))
        } else {
            message.error(t('care_status.change_position_failed'))
        }
        getList()

    }

    useEffect(() => {
        getList()
    }, [sortParam, page])

    // useEffect(() => {
    //     console.log(data)
    // },[data])

    return (
        <Layout>
            <Helmet>
                <title>{t('page.care_status')} | Khothi</title>
            </Helmet>
            <div className="">
                <div className="flex gap-2 flex-col md:flex-row md:justify-between mb-4">
                    <h2 className="text-lg font-semibold t-text-white">
                        {t('care_status.title')}
                    </h2>

                    <Space>
                        {/* Đổi vị trí là thao tác sửa dữ liệu -> theo quyền edit */}
                        {canEdit && (
                            <Button
                                onClick={() => setPositionOpen(true)}
                                className="border-gray-300 dark:border-gray-600 t-text-white hover:bg-gray-700 dark:hover:text-white dark:hover:!bg-gray-700 bg-transparent"
                            >
                                {t('care_status.change_position')}
                            </Button>
                        )}
                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openAdd}
                                className="!bg-[#3e91ab] hover:!bg-[#357f96] border-none"
                            >
                                {t('care_status.add_status')}
                            </Button>
                        )}
                        <ColumnSettingButton {...columnSettingProps} />
                    </Space>
                </div>

                <Table
                    loading={loading}
                    rowKey="id"
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                    onChange={(_pagination, _filters, sorter: any) => {
                        const nextSort = sorter?.columnKey && sorter?.order
                            ? `${sorter.columnKey},${sorter.order === 'ascend' ? 'asc' : 'desc'}`
                            : 'position,asc';

                        // onChange bắn cả khi đổi trang. Chỉ về trang 1 khi thật sự
                        // đổi sắp xếp, không thì nó ghi đè trang vừa chọn.
                        if (nextSort !== sortParam) {
                            setSortParam(nextSort);
                            setPage(1);
                        }
                    }}
                    pagination={{
                        current: page,
                        total: total,
                        pageSize: PAGE_SIZE,
                        onChange: (p) => setPage(p)
                    }}
                />

                <TagModal
                    open={modalOpen}
                    mode={mode}
                    initialValues={
                        editingItem
                            ? {
                                name: editingItem.name,
                                color: editingItem.color,
                                description: editingItem.description,
                                position: editingItem.position,
                                code: editingItem.code,
                            }
                            : undefined
                    }
                    onCancel={() => setModalOpen(false)}
                    onSubmit={handleSubmit}
                />
                <ChangePositionModal
                    open={positionOpen}
                    data={data}
                    onCancel={() => setPositionOpen(false)}
                    onSave={(newData) => { changePosition(newData) }}
                />

                {/* Action Modal */}
                {actionModal.open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl w-full max-w-md">
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-bold text-gray-900 t-text-white">
                                    {t('care_status.delete_title')}
                                </h3>
                                <p className="text-sm text-gray-500 text-000000e0 mt-1">
                                    {t('care_status.delete_confirm')}
                                </p>
                            </div>
                            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                                <button
                                    onClick={handleCloseActionModal}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg !text-gray-700 dark:!text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                >
                                    {t('care_status.cancel')}
                                </button>
                                <button
                                    onClick={handleConfirmAction}
                                    className={`px-4 py-2 !text-white rounded-lg transition ${actionModal.type === 'approve'
                                        ? 'bg-emerald-600 hover:bg-emerald-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                >
                                    {t('care_status.delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}